/**
 * Graph-database schema (GraphNodeType / GraphEdgeType / GraphIndex) — the rules the screens share, as plain functions.
 *
 * The model is flat: node types, edge types and indexes sit side by side under a DataLake and nothing links them. An edge
 * type names its two node types as plain text. A node or edge type's `properties` are a list on the type, not nodes of
 * their own, and the backend REPLACES the whole list on every update, so the editor always sends the complete list.
 *
 * Kept free of React so the same checks can run anywhere. They mirror the backend's (utils/graphdb-schema.ts), which has
 * the final say; these only save a round trip.
 */

export const GRAPH_DB_KINDS = ["node-type", "edge-type", "index"] as const;
export type GraphDbKind = (typeof GRAPH_DB_KINDS)[number];

/** The route segment for each kind (`/db-ontology/{slug}/{id}`). */
export const GRAPH_DB_SLUG: Record<GraphDbKind, string> = {
  "node-type": "graph-node-type",
  "edge-type": "graph-edge-type",
  index: "graph-index",
};

/** The graph's own label for each kind — what a search hit, the history and the graph view call it. */
export const GRAPH_DB_LABEL: Record<GraphDbKind, string> = {
  "node-type": "GraphNodeType",
  "edge-type": "GraphEdgeType",
  index: "GraphIndex",
};

export const GRAPH_DB_KIND_TITLE: Record<GraphDbKind, string> = {
  "node-type": "Node types",
  "edge-type": "Edge types",
  index: "Indexes",
};

export const GRAPH_INDEX_KINDS = ["range", "unique", "composite", "fulltext", "vector", "existence"] as const;
export type GraphIndexKind = (typeof GRAPH_INDEX_KINDS)[number];

/** Upper bound on one type's property list, as the backend has it. */
export const MAX_GRAPH_PROPERTIES = 200;

export interface GraphDbProperty {
  name: string;
  type?: string;
  required?: boolean;
}

/** Trimmed and lower-cased: how names are compared (the backend's natural keys are case-insensitive). */
export const graphKey = (value: unknown): string => String(value ?? "").trim().toLowerCase();

/** A stored or received `properties` value (a list, or its JSON string) as a list. Never throws. */
export function toProperties(value: unknown): GraphDbProperty[] {
  let list: unknown = value;
  if (typeof value === "string") {
    try {
      list = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list
    .filter((p): p is Record<string, unknown> => !!p && typeof p === "object" && !Array.isArray(p))
    .map((p) => ({
      name: String(p.name ?? ""),
      ...(typeof p.type === "string" && p.type ? { type: p.type } : {}),
      ...(typeof p.required === "boolean" ? { required: p.required } : {}),
    }));
}

/** The list to send: trimmed, and rows left entirely blank dropped (an editor always keeps a spare row to type in). */
export function cleanProperties(rows: GraphDbProperty[]): GraphDbProperty[] {
  return rows
    .map((r) => ({
      name: r.name.trim(),
      ...(r.type?.trim() ? { type: r.type.trim() } : {}),
      ...(r.required ? { required: true } : {}),
    }))
    .filter((r) => r.name || r.type || r.required);
}

/** The first problem with a property list, naming the row; null when it is fine. Rows are 1-based in the message. */
export function propertiesProblem(rows: GraphDbProperty[]): string | null {
  const list = cleanProperties(rows);
  if (list.length > MAX_GRAPH_PROPERTIES) return `At most ${MAX_GRAPH_PROPERTIES} properties`;
  const seen = new Map<string, number>();
  for (let i = 0; i < list.length; i++) {
    if (!list[i].name) return `Property ${i + 1} needs a name`;
    const key = graphKey(list[i].name);
    if (seen.has(key)) return `Property ${i + 1} ("${list[i].name}") repeats property ${seen.get(key)}`;
    seen.set(key, i + 1);
  }
  return null;
}

/** "id: string (required), email: string" — a property list on one line, for a row in a list. */
export function describeProperties(rows: GraphDbProperty[]): string {
  return rows
    .filter((p) => p.name)
    .map((p) => `${p.name}${p.type ? `: ${p.type}` : ""}${p.required ? " (required)" : ""}`)
    .join(", ");
}

export interface GraphDbForm {
  name: string;
  description: string;
  properties: GraphDbProperty[];
  from: string;
  to: string;
  indexKind: string;
  definition: string;
}

export const EMPTY_GRAPH_DB_FORM: GraphDbForm = {
  name: "", description: "", properties: [], from: "", to: "", indexKind: "", definition: "",
};

/** The first thing wrong with a form for this kind, or null when it can be saved. */
export function formProblem(kind: GraphDbKind, form: GraphDbForm): string | null {
  if (!form.name.trim()) return "Name is required";
  if (kind === "index") {
    if (!(GRAPH_INDEX_KINDS as readonly string[]).includes(form.indexKind)) return "Choose a kind";
    if (!form.definition.trim()) return "Definition is required";
    return null;
  }
  if (kind === "edge-type") {
    if (!form.from.trim()) return "Choose the node type this relationship starts from";
    if (!form.to.trim()) return "Choose the node type this relationship points to";
  }
  return propertiesProblem(form.properties);
}

/** The request body for a save, with only what this kind has. Properties go as the complete list. */
export function formPayload(kind: GraphDbKind, form: GraphDbForm): Record<string, unknown> {
  if (kind === "index") {
    return { name: form.name.trim(), kind: form.indexKind, definition: form.definition.trim() };
  }
  return {
    name: form.name.trim(),
    description: form.description.trim() || undefined,
    properties: cleanProperties(form.properties),
    ...(kind === "edge-type" ? { from: form.from.trim(), to: form.to.trim() } : {}),
  };
}
